import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { constants } from 'src/environments/constants';
import { environment } from 'src/environments/non-vendor/environment';

export interface ILocaleInfo {
  localeName: string;
  isEnglish: boolean;
  isUnitedStatesStandardEnglishLocale: boolean;
  language: string;
  countryCode: string;
  region: string;
}

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  private localeInfo: ILocaleInfo = {localeName: "", isEnglish: false, isUnitedStatesStandardEnglishLocale: false, language: "", countryCode: "", region: ""};
  private readonly unitedStatesStandardEnglishLocales: string[] = [constants.EN_US_LOCALE, constants.EN_CA_LOCALE];

  constructor(private httpClient: HttpClient, private route: ActivatedRoute) { }

  fileExists(url: string): Observable<boolean> {
    return this.httpClient.head(url).pipe(map(() => true), catchError(() => of(false)));
  }

  openUrlInNewTab(url: string): void {
    window.open(url, '_blank');
  }

  getLocaleInfo(localeQueryParamKey: string = constants.LOCALE_QUERY_PARAM): ILocaleInfo {
    if (!this.localeInfo.localeName) {
      const locale: string | null = this.route.snapshot.queryParamMap.get(localeQueryParamKey);

      const localeName = !!locale ? locale : constants.EN_US_LOCALE;
      const language = localeName.split("_")[0].toLowerCase();
      const countryCode = localeName.split("_")[1].toLowerCase();
      const region = this.getRegion(countryCode);
      const isEnglish = language === constants.EN_LANGUAGE;
      const isUnitedStatesStandardEnglishLocale = this.unitedStatesStandardEnglishLocales.includes(localeName);

      this.localeInfo = {
        localeName: localeName,
        isEnglish: isEnglish,
        isUnitedStatesStandardEnglishLocale: isUnitedStatesStandardEnglishLocale,
        language: language,
        countryCode: countryCode,
        region: region
      };
    }

    return this.localeInfo;
  }

  private getRegion(country: string): string {
    switch (country) {
      case constants.GB_COUNTRY:
        return constants.UK_REGION;
      default:
        return country;
    }
  }

  getLocalizedUrl(url: string, environmentName: string = ""): string {
    switch (environment.name) {
      case constants.INSIGHT:
      case constants.INSIGHT_EDU:
        const localeInfo = this.getLocaleInfo();

        if (localeInfo.localeName === constants.EN_US_LOCALE) {
          return url;

        } else if (environmentName && (environmentName === constants.STAGE)) {
          return url.replace(constants.SIM_SUBDOMAIN, `${localeInfo.region}-${constants.SIM_SUBDOMAIN}`).replace(constants.EN_US_LOCALE, localeInfo.localeName);

        } else if (localeInfo.region === constants.UK_REGION) {
          // Note: UK product URLs for non-english languages are not in a consistent format, so just route to the english URL which is consistent.

          return url.replace(constants.DEFAULT_SUBDOMAIN, localeInfo.region).replace(constants.EN_US_LOCALE, constants.EN_GB_LOCALE);

        } else {
          return url.replace(constants.DEFAULT_SUBDOMAIN, localeInfo.region).replace(constants.EN_US_LOCALE, localeInfo.localeName);
        }
      case constants.CDW:
        return url;
      case constants.DANDH:
        return url;
      case constants.SYNNEX:
        return url;
      case constants.SYNNEX_Public:
        return url;
      case constants.BLUUM:
        return url;
      case constants.CONNECTION:
        return url;
      case constants.CONNECTION_GOV:
        return url;
      case constants.TRAFERA:
        return url;
      case constants.SYNNEX_AUS:
        return url;
      case constants.VIVACITY:
        return url;
      case constants.PROMEVO:
        return url;
      case constants.DEMO:
      case constants.DEMO_EDU:
        return "";
      default:
        return "";
    }
  }
}
